// Plans.jsx
import './Plans.scss';

import React, { useEffect, useMemo, useState } from 'react';

import AppLoading from '../../commons/AppLoading';
import HallAppToast from '../../commons/HallAppToast';
import SignatureAPI from './SignatureAPI';
import SubscriptionModal from './SubscriptionModal';
import { formatDate } from '../../commons/util/HallAppUtil';

const SINGLE_FEATURES = [
    // Essencial
    'Acesso a comandas',
    'Agendamento',
    'Fechamento de caixa',
    'Gerenciamento de estabelecimentos',
    'Gerenciamento das maquininhas',
    'Categorias e serviços',
    'Clientes',
    'Relatórios',

    // Extras (incluídos em TODOS os planos agora)
    'Hallie consultoria digital com IA',
    'Notificações via WhatsApp',
    'Gerador de reels com base no perfil do estabelecimento - versão 1.0',
];

const ALL_FEATURES = [
    // Essencial
    'Acesso a comandas',
    'Agendamento',
    'Fechamento de caixa',
    'Gerenciamento de estabelecimentos',
    'Gerenciamento de funcionários',
    'Gerenciamento das maquininhas',
    'Categorias e serviços',
    'Clientes',
    'Relatórios',

    // Extras (incluídos em TODOS os planos agora)
    'Hallie consultoria digital com IA',
    'Notificações via WhatsApp',
    'Gerador de reels com base no perfil do estabelecimento',
];

const plans = [
    {
        id: 1,
        name: 'Plano Individual',
        features: SINGLE_FEATURES,
        price: 'R$ 69',
        description: 'Para 1 usuário (apenas você).',
        planCode: 'INDIVIDUAL', // ajuste se seu backend usar outro código
    },
    {
        id: 2,
        name: 'Plano Colaboradores',
        features: ALL_FEATURES,
        price: 'R$ 79',
        description: 'Ilimitado a quantidade de colaboradores.',
        planCode: 'COLLABORATORS', // ajuste se seu backend usar outro código
    },
];

const signatureAPI = new SignatureAPI();

function Plans() {
    const [showModal, setShowModal] = useState(false);
    const [plan, setPlan] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [signature, setSignature] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        getContract();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function getContract() {
        try {
            setIsLoading(true);
            const resp = await signatureAPI.getMySignature();
            setSignature(resp.data);
            setError(false);
        } catch (e) {
            console.error('Erro ao buscar assinatura:', e);
            HallAppToast.error();
            setSignature(null);
            setError(true);
        } finally {
            setIsLoading(false);
        }
    }

    const userHasActivePlan =
        signature &&
        signature.contractStatus === 'ACTIVE' &&
        signature.plan != null;

    const currentPlan = useMemo(() => {
        return plans.find((p) => p.planCode === signature?.plan);
    }, [signature?.plan]);

    const openWhatsAppChangePlan = () => {
        const phone = '5519981041860';
        const current = currentPlan?.name ? ` (atual: ${currentPlan.name})` : '';
        const message = encodeURIComponent(`Gostaria de alterar meu plano${current}`);
        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    };

    const openWhatsAppCancelPlan = () => {
        const phone = '5519981041860';
        const current = currentPlan?.name ? ` (atual: ${currentPlan.name})` : '';
        const message = encodeURIComponent(`Gostaria de cancelar meu plano${current}`);
        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    };

    function renderActiveSubscription() {
        return (
            <main className="plans-page" role="main">
                <div className="active-subscription-wrapper">
                    <h1 className="plans-title">Assinatura ativa</h1>

                    <section className="active-subscription-card">
                        <div className="info-row">
                            <span className="label">Plano:</span>
                            <span className="value">{currentPlan?.name || 'Plano Atual'}</span>
                        </div>

                        <div className="info-row">
                            <span className="label">Data da assinatura:</span>
                            <span className="value">{formatDate(signature.contractDate)}</span>
                        </div>

                        <div className="info-row">
                            <span className="label">Valor:</span>
                            <span className="value">
                                {currentPlan?.price
                                    ? `${currentPlan.price}/mês`
                                    : signature?.value != null
                                        ? `R$ ${signature.value}/mês`
                                        : '--'}
                            </span>
                        </div>

                        <div className="info-row">
                            <span className="label">Status:</span>
                            <span className={`value status ${signature.contractStatus.toLowerCase()}`}>
                                {signature.contractStatus === 'ACTIVE' ? 'ATIVO' : 'INATIVO'}
                            </span>
                        </div>

                        <div className="btn-group">
                            <button
                                className="btn-action"
                                onClick={openWhatsAppChangePlan}
                                aria-label="Alterar plano via WhatsApp"
                                type="button"
                            >
                                Mudar plano
                            </button>
                            <button
                                className="btn-action btn-cancel"
                                onClick={openWhatsAppCancelPlan}
                                aria-label="Cancelar plano via WhatsApp"
                                type="button"
                            >
                                Cancelar plano
                            </button>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    function renderPlansList() {
        return (
            <main className="plans-page" role="main">
                <h1 className="plans-title">Planos do Engatize</h1>

                <p className="plans-subtitle">
                    Escolha o plano ideal. Todos incluem todos os recursos do sistema e podem ser cancelados a qualquer momento.
                </p>

                <section className="plans-container" aria-label="Lista de planos">
                    {plans.map((p) => (
                        <article
                            key={p.id}
                            className="plan-card"
                            tabIndex={0}
                            aria-labelledby={`plan-title-${p.id}`}
                        >
                            <header className="plan-header">
                                <h2 id={`plan-title-${p.id}`} className="plan-name">
                                    {p.name}
                                </h2>
                                <p className="plan-price">{p.price} / mês</p>
                                <p className="plan-description">{p.description}</p>
                            </header>

                            <ul className="plan-features" aria-label={`Recursos do ${p.name}`}>
                                {p.features.map((feature, idx) => (
                                    <li key={idx} className="plan-feature">
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => {
                                    setPlan(p);
                                    setShowModal(true);
                                }}
                                className="btn-subscribe"
                                type="button"
                                aria-label={`Assinar ${p.name}`}
                            >
                                Assinar {p.name}
                            </button>
                        </article>
                    ))}
                </section>

                {showModal && (
                    <SubscriptionModal
                        show={showModal}
                        plan={plan}
                        closeShow={() => setShowModal(false)}
                        call={getContract}
                    />
                )}
            </main>
        );
    }

    function renderError() {
        return (
            <main className="plans-page" role="main">
                <div className="error-wrapper">
                    <h1 className="error-message">Erro ao carregar sua assinatura</h1>
                    <p className="error-submessage">
                        Tente novamente mais tarde ou entre em contato com o suporte.
                    </p>
                </div>
            </main>
        );
    }

    return (
        <>
            {isLoading && <AppLoading />}
            {!isLoading && error && renderError()}
            {!isLoading && !error && userHasActivePlan && renderActiveSubscription()}
            {!isLoading && !error && !userHasActivePlan && renderPlansList()}
        </>
    );
}

export default Plans;
